import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, ArrowRight, X } from "lucide-react";
import api from "../../lib/api";

// Premium SaaS-style dashboard banner. Fetches the newest active announcement
// from /api/announcements/active and renders it as a single dismissible card.
// Dismissal is local-only (sessionStorage keyed by announcement id) so the
// user isn't nagged repeatedly within a session, but a fresh session/login
// will re-show it. Admin can flip is_active=false to retire it for everyone.
export const AnnouncementBanner = () => {
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/announcements/active");
        if (cancelled) return;
        const next = Array.isArray(data) && data.length > 0 ? data[0] : null;
        setAnnouncement(next);
        if (next) {
          const key = `tasklio_announcement_dismissed_${next.id}`;
          if (sessionStorage.getItem(key) === "1") setDismissed(true);
        }
      } catch {
        // Silent — banner is non-critical. Don't surface to users.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!announcement || dismissed) return null;

  const onDismiss = () => {
    try {
      sessionStorage.setItem(
        `tasklio_announcement_dismissed_${announcement.id}`,
        "1",
      );
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const onCta = () => {
    const url = announcement.cta_url;
    if (!url) return;
    if (url.startsWith("/")) {
      navigate(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className="mt-6 rounded-2xl bg-gradient-to-r from-emerald-50 via-white to-white border border-emerald-200 border-l-4 border-l-emerald-600 shadow-sm p-5 flex items-start gap-3"
      data-testid="announcement-banner"
    >
      <span className="w-9 h-9 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
        <Megaphone className="w-4 h-4 text-emerald-700" />
      </span>
      <div className="flex-1 min-w-0">
        <p
          className="font-semibold text-gray-900"
          data-testid="announcement-banner-title"
        >
          {announcement.title}
        </p>
        <p
          className="text-sm text-gray-600 mt-0.5"
          data-testid="announcement-banner-message"
        >
          {announcement.message}
        </p>
        {announcement.cta_text && announcement.cta_url ? (
          <button
            type="button"
            onClick={onCta}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:text-emerald-900"
            data-testid="announcement-banner-cta"
          >
            {announcement.cta_text}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1.5 -mr-1 -mt-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-emerald-100/50"
        aria-label="Dismiss announcement"
        data-testid="announcement-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
